
import { AssessmentResult, FileData, OllamaConfig } from "../types";
import { extractTextFromFile } from "../utils/fileParser";

const DEFAULT_ASSESSMENT: Partial<AssessmentResult> = {
  overallMark: 0,
  minMark: 0,
  maxMark: 0,
  classification: "Fail",
  criteriaResults: [],
  generalStrengths: [],
  areasForImprovement: [],
  summaryFeedback: "No summary feedback provided."
};

export const fetchOllamaModels = async (baseUrl: string): Promise<string[]> => {
  try {
    const response = await fetch(`${baseUrl}/api/tags`, {
      method: 'GET',
      mode: 'cors',
      cache: 'no-cache'
    });
    if (!response.ok) return [];
    const data = await response.json();
    return data.models?.map((m: any) => m.name) || [];
  } catch (err) {
    console.error("Error fetching Ollama models:", err);
    return [];
  }
};

export const testOllamaConnection = async (baseUrl: string): Promise<boolean> => {
  try {
    const response = await fetch(`${baseUrl}/api/tags`, {
      method: 'GET',
      mode: 'cors',
      cache: 'no-cache'
    });
    return response.ok;
  } catch (err) {
    return false;
  }
};

export const assessWithOllama = async (
  rubricFile: FileData,
  assignmentFile: FileData,
  config: OllamaConfig,
  extraContext: string = ''
): Promise<AssessmentResult> => {
  const rubricText = await extractTextFromFile(rubricFile.base64, rubricFile.mimeType);
  const assignmentText = await extractTextFromFile(assignmentFile.base64, assignmentFile.mimeType);
  const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

  const prompt = `
    You are a Senior Academic in Pharmacy and Pharmaceutical Sciences at the University of Nottingham.
    Evaluate the following student work against the provided rubric with academic rigour.
    
    Today's date is ${today}. Only flag references as "in the future" if they are dated AFTER ${today}.

    TECHNICAL CONSTRAINTS:
    - You are reading a TEXT-ONLY extraction. Images/figures/formatting are NOT visible.
    - RUBRIC INTERPRETATION: The rubric may be a text extraction from a table or a structured JSON object. If JSON, map 'criteria' to 'criteriaResults'. The 'marks' field in JSON represents the maximum marks for that criterion.
    - If the rubric contains criteria for 'Figures', 'Images', 'Chemical Structures', or 'Formatting/Presentation', award a neutral mid-range mark and explain that visual elements could not be directly verified.

    UK MARKING SCALES (NOTTINGHAM):
    - 70%+: First Class (1st)
    - 60-69%: Upper Second (2:1)
    - 50-59%: Lower Second (2:2)
    - 40-49%: Third Class (3rd)
    - <40%: Fail

    ${extraContext ? `ADDITIONAL ACADEMIC CONTEXT FOR THIS ASSESSMENT:\n${extraContext}\n` : ''}

    RUBRIC:
    ${rubricText}

    STUDENT WORK (TEXT EXTRACTION):
    ${assignmentText}

    RESPONSE FORMAT:
    You must return ONLY a JSON object with overallMark, minMark, maxMark, classification, criteriaResults (array of objects with: criterion, mark, maxMark, feedback, isVisual), generalStrengths (array), areasForImprovement (array), and summaryFeedback. Use UK English spelling (e.g., 'organised', 'synthesised').
  `;

  try {
    const response = await fetch(`${config.baseUrl}/api/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      mode: 'cors',
      body: JSON.stringify({
        model: config.model,
        messages: [{ role: 'user', content: prompt }],
        stream: false,
        format: 'json'
      }),
    });

    if (!response.ok) {
      const errorBody = await response.text().catch(() => 'No error body');
      console.error(`Ollama Server Error: ${response.status}`, errorBody);
      throw new Error(`Ollama server error (${response.status}): ${errorBody}`);
    }

    const data = await response.json();
    if (!data.message || !data.message.content) {
      throw new Error("Invalid response format from Ollama.");
    }

    const rawResult = JSON.parse(data.message.content);
    const criteriaResults = Array.isArray(rawResult.criteriaResults) ? rawResult.criteriaResults : [];
    
    // Calculate raw totals and percentage
    const totalEarned = criteriaResults.reduce((sum: number, c: any) => sum + (Number(c.mark) || 0), 0);
    let totalMax = criteriaResults.reduce((sum: number, c: any) => sum + (Number(c.maxMark) || 0), 0);
    
    // Fallback if maxMark is only provided at the top level
    if (totalMax === 0 && rawResult.maxMark) {
      totalMax = Number(rawResult.maxMark);
    }
    
    const overallMark = totalMax > 0 ? Math.round((totalEarned / totalMax) * 100) : (Number(rawResult.overallMark) || 0);

    const result: AssessmentResult = { 
      ...DEFAULT_ASSESSMENT, 
      ...rawResult,
      criteriaResults,
      totalEarned,
      totalMax,
      overallMark,
      generalStrengths: Array.isArray(rawResult.generalStrengths) ? rawResult.generalStrengths : [],
      areasForImprovement: Array.isArray(rawResult.areasForImprovement) ? rawResult.areasForImprovement : []
    };
    result.fileName = assignmentFile.name;
    return result;
  } catch (err: any) {
    console.error("Ollama Fetch Error:", err);
    throw err;
  }
};
